import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/client/components/ui/command';
import { useTheme } from '@/client/hooks/useTheme';
import { supabase } from '@/client/lib/supabase';
import { trpc } from '@/client/utils/trpc';
import { useRouter } from '@tanstack/react-router';
import { FilePlus2, LogOut, Moon, PlusCircle, Sun } from 'lucide-react';
import { useEffect } from 'react';
import { SETTINGS_ITEM, getNavSections } from './nav';

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Global ⌘K palette: jump to any page or run a quick action. */
export function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const router = useRouter();
  const { data: me } = trpc.user.me.useQuery();
  const isAdmin = me?.role === 'ADMIN';
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  function run(fn: () => void) {
    onOpenChange(false);
    fn();
  }

  const go = (path: string) => run(() => router.history.push(path));
  const sections = getNavSections(isAdmin);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages and actions…" />
      <CommandList className="max-h-[380px]">
        <CommandEmpty>No results found.</CommandEmpty>
        {isAdmin && (
          <>
            <CommandGroup heading="Quick actions">
              <CommandItem value="new invoice create" onSelect={() => go('/invoices/new')}>
                <FilePlus2 />
                New invoice
              </CommandItem>
              <CommandItem
                value="new finance record transaction expense income"
                onSelect={() => go('/finance/new')}
              >
                <PlusCircle />
                New finance record
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {[...sections, { label: 'Account', items: [SETTINGS_ITEM] }].map((section) => (
          <CommandGroup key={section.label} heading={section.label}>
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.to}
                  value={`${item.label} ${item.keywords ?? ''}`}
                  onSelect={() => go(item.to)}
                >
                  <Icon />
                  {item.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
        <CommandSeparator />
        <CommandGroup heading="Preferences">
          <CommandItem value="toggle theme dark light mode" onSelect={() => run(toggleTheme)}>
            {isDark ? <Sun /> : <Moon />}
            {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          </CommandItem>
          <CommandItem
            value="log out sign out"
            onSelect={() =>
              run(async () => {
                await supabase.auth.signOut();
                router.history.push('/login');
              })
            }
          >
            <LogOut />
            Log out
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <div className="flex items-center justify-between border-t bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
        <span>Navigate with ↑ ↓ · Enter to open</span>
        <CommandShortcut className="ml-0">Esc to close</CommandShortcut>
      </div>
    </CommandDialog>
  );
}
